import React, { useState, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { 
  User, Phone, Calendar, Shield, Mail, Building, CreditCard, 
  Edit, Save, X, Lock, Upload, FileText, Check, AlertCircle, KeyRound, Eye, EyeOff
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

export const SettingsSection = () => {
  const { user, api, refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [showOTPDialog, setShowOTPDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpHint, setOtpHint] = useState('');
  const [pendingUpdates, setPendingUpdates] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const docInputRef = useRef(null);

  // Password state
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [passwordOtp, setPasswordOtp] = useState('');
  const [passwordOtpHint, setPasswordOtpHint] = useState('');
  const [otpRequested, setOtpRequested] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  const [formData, setFormData] = useState({
    name: user?.name || '',
    surname: user?.surname || '',
    email: user?.email || '',
    mobile: user?.mobile || '',
    id_number: user?.id_number || '',
    place_of_work: user?.place_of_work || '',
    union_name: user?.union_name || '',
    gender: user?.gender || ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleStartEdit = () => {
    setFormData({
      name: user?.name || '',
      surname: user?.surname || '',
      email: user?.email || '',
      mobile: user?.mobile || '',
      id_number: user?.id_number || '',
      place_of_work: user?.place_of_work || '',
      union_name: user?.union_name || '',
      gender: user?.gender || ''
    });
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setFormData({
      name: user?.name || '',
      surname: user?.surname || '',
      email: user?.email || '',
      mobile: user?.mobile || '',
      id_number: user?.id_number || '',
      place_of_work: user?.place_of_work || '',
      union_name: user?.union_name || '',
      gender: user?.gender || ''
    });
  };

  const handleSubmitChanges = async () => {
    // Collect changed fields
    const changes = {};
    const original = {
      name: user?.name || '',
      surname: user?.surname || '',
      email: user?.email || '',
      mobile: user?.mobile || '',
      id_number: user?.id_number || '',
      place_of_work: user?.place_of_work || '',
      union_name: user?.union_name || '',
      gender: user?.gender || ''
    };

    for (const [key, value] of Object.entries(formData)) {
      if (value !== original[key] && value !== '') {
        changes[key] = value;
      }
    }

    if (Object.keys(changes).length === 0) {
      toast.info('No changes detected');
      return;
    }

    setPendingUpdates(changes);

    // Request OTP
    try {
      const res = await api.post('/user/profile/request-otp');
      setOtpHint(res.data.otp_hint);
      setShowOTPDialog(true);
      toast.info('An OTP has been sent to verify your changes');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request OTP');
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length < 4) {
      toast.error('Please enter a valid OTP');
      return;
    }

    setSubmitting(true);
    try {
      await api.put('/user/profile', {
        otp: otp,
        updates: pendingUpdates
      });
      toast.success('Profile updated successfully!');
      setShowOTPDialog(false);
      setOtp('');
      setEditing(false);
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify OTP');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDocUpload = (e) => {
    const files = Array.from(e.target.files || []);
    const newDocs = files.map(file => ({
      name: file.name,
      size: (file.size / 1024).toFixed(1) + ' KB',
      type: file.type,
      file
    }));
    setUploadedDocs(prev => [...prev, ...newDocs]);
    toast.success(`${files.length} document(s) added`);
  };

  const removeDoc = (index) => {
    setUploadedDocs(prev => prev.filter((_, i) => i !== index));
  };

  const handleRequestPasswordOTP = async () => {
    if (!passwordData.current) {
      toast.error('Enter your current password');
      return;
    }
    if (!passwordData.new || passwordData.new.length < 4) {
      toast.error('New password must be at least 4 characters');
      return;
    }
    if (passwordData.new !== passwordData.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    try {
      const res = await api.post('/user/password/request-otp');
      setPasswordOtpHint(res.data.otp_hint || '');
      setOtpRequested(true);
      toast.success('OTP sent to your mobile');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request OTP');
    }
  };

  const handleChangePassword = async () => {
    if (!passwordOtp || passwordOtp.length < 4) {
      toast.error('Enter a valid OTP');
      return;
    }
    setSubmitting(true);
    try {
      await api.put('/user/password', {
        current_password: passwordData.current,
        new_password: passwordData.new,
        otp: passwordOtp
      });
      toast.success('Password changed successfully!');
      setShowPasswordDialog(false);
      setPasswordData({ current: '', new: '', confirm: '' });
      setPasswordOtp('');
      setOtpRequested(false);
      setPasswordOtpHint('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  const kycBadge = () => {
    const styles = {
      approved: 'bg-green-100 text-green-800',
      submitted: 'bg-blue-100 text-blue-800',
      rejected: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[user?.kyc_status] || styles.pending}`}>
        {user?.kyc_status}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="settings-section">
      <div className="flex items-center justify-between">
        <h1 className="font-syne text-2xl font-bold text-gray-900">Profile & Settings</h1>
        {!editing ? (
          <Button
            onClick={handleStartEdit}
            className="bg-afriperx-green hover:bg-afriperx-green-dark text-white"
            data-testid="edit-profile-btn"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit Profile
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              onClick={handleCancelEdit}
              variant="outline"
              className="border-gray-300 text-gray-600"
              data-testid="cancel-edit-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSubmitChanges}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="save-profile-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </div>
        )}
      </div>

      {/* Profile Header */}
      <Card className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-afriperx-green/10 flex items-center justify-center">
            <span className="text-afriperx-gold font-bold text-2xl">
              {user?.name?.charAt(0)}{user?.surname?.charAt(0)}
            </span>
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{user?.name} {user?.surname}</h2>
            <p className="text-gray-500">{user?.mobile}</p>
            <div className="mt-1">{kycBadge()}</div>
          </div>
        </div>
      </Card>

      {/* Personal Details */}
      <Card className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
        <h2 className="font-syne text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-afriperx-gold" />
          Personal Information
        </h2>

        {editing ? (
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700">First Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="mt-1 bg-white border-gray-300 text-gray-900"
                  data-testid="profile-name-input"
                />
              </div>
              <div>
                <Label className="text-gray-700">Surname</Label>
                <Input
                  value={formData.surname}
                  onChange={(e) => handleInputChange('surname', e.target.value)}
                  className="mt-1 bg-white border-gray-300 text-gray-900"
                  data-testid="profile-surname-input"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700">Email Address</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="your@email.com"
                  className="mt-1 bg-white border-gray-300 text-gray-900"
                  data-testid="profile-email-input"
                />
              </div>
              <div>
                <Label className="text-gray-700">Telephone Number</Label>
                <Input
                  value={formData.mobile}
                  onChange={(e) => handleInputChange('mobile', e.target.value)}
                  className="mt-1 bg-white border-gray-300 text-gray-900"
                  data-testid="profile-mobile-input"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700">ID Number</Label>
                <Input
                  value={formData.id_number}
                  onChange={(e) => handleInputChange('id_number', e.target.value)}
                  placeholder="Enter your ID number"
                  className="mt-1 bg-white border-gray-300 text-gray-900"
                  data-testid="profile-id-input"
                />
              </div>
              <div>
                <Label className="text-gray-700">Gender</Label>
                <Select value={formData.gender} onValueChange={(v) => handleInputChange('gender', v)}>
                  <SelectTrigger className="mt-1 bg-white border-gray-300 text-gray-900" data-testid="profile-gender-select">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700">Place of Work</Label>
                <Input
                  value={formData.place_of_work}
                  onChange={(e) => handleInputChange('place_of_work', e.target.value)}
                  placeholder="Enter your employer"
                  className="mt-1 bg-white border-gray-300 text-gray-900"
                  data-testid="profile-work-input"
                />
              </div>
              <div>
                <Label className="text-gray-700">Union Name</Label>
                <Input
                  value={formData.union_name}
                  onChange={(e) => handleInputChange('union_name', e.target.value)}
                  placeholder="Enter union name"
                  className="mt-1 bg-white border-gray-300 text-gray-900"
                  data-testid="profile-union-input"
                />
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
              <Lock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-yellow-800 text-sm">
                You will need to verify your identity with an OTP before changes are saved.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { icon: User, label: 'Full Name', value: `${user?.name || ''} ${user?.surname || ''}` },
              { icon: Mail, label: 'Email Address', value: user?.email || 'Not set' },
              { icon: Phone, label: 'Telephone Number', value: user?.mobile || 'Not set' },
              { icon: CreditCard, label: 'ID Number', value: user?.id_number || 'Not set' },
              { icon: Building, label: 'Place of Work', value: user?.place_of_work || 'Not set' },
              { icon: User, label: 'Union Name', value: user?.union_name || 'Not set' },
              { icon: User, label: 'Gender', value: user?.gender || 'Not set' },
              { icon: Calendar, label: 'Member Since', value: user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A' },
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <Icon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <div>
                    <p className="text-gray-500 text-xs">{item.label}</p>
                    <p className={`text-gray-900 ${item.value === 'Not set' ? 'text-gray-400 italic' : ''}`}>
                      {item.value}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Document Upload Section */}
      <Card className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
        <h2 className="font-syne text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-afriperx-gold" />
          Documents
        </h2>
        <p className="text-gray-500 text-sm mb-4">
          Upload any additional documents such as payslips, bank statements, or proof of employment.
        </p>

        <input
          type="file"
          ref={docInputRef}
          onChange={handleDocUpload}
          accept="image/*,.pdf,.doc,.docx"
          multiple
          className="hidden"
          data-testid="doc-upload-input"
        />

        <Button
          onClick={() => docInputRef.current?.click()}
          variant="outline"
          className="border-gray-300 text-gray-700 hover:bg-gray-50 mb-4"
          data-testid="upload-doc-btn"
        >
          <Upload className="w-4 h-4 mr-2" />
          Upload Document
        </Button>

        {uploadedDocs.length > 0 && (
          <div className="space-y-2">
            {uploadedDocs.map((doc, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="text-gray-900 text-sm font-medium">{doc.name}</p>
                    <p className="text-gray-500 text-xs">{doc.size}</p>
                  </div>
                </div>
                <button
                  onClick={() => removeDoc(idx)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {uploadedDocs.length === 0 && (
          <p className="text-gray-400 text-sm text-center py-4">No documents uploaded yet</p>
        )}
      </Card>

      {/* Account Summary */}
      <Card className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
        <h2 className="font-syne text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-afriperx-gold" />
          Account Summary
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <p className="text-2xl font-bold text-gray-900">R{user?.main_balance?.toFixed(0) || 0}</p>
            <p className="text-gray-500 text-xs">Main Balance</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <p className="text-2xl font-bold text-gray-900">R{user?.savings_balance?.toFixed(0) || 0}</p>
            <p className="text-gray-500 text-xs">Savings</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <p className="text-2xl font-bold text-gray-900">{user?.loyalty_points?.toLocaleString() || 0}</p>
            <p className="text-gray-500 text-xs">Loyalty Points</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <p className="text-2xl font-bold text-gray-900 capitalize">{user?.kyc_status || 'pending'}</p>
            <p className="text-gray-500 text-xs">KYC Status</p>
          </div>
        </div>
      </Card>

      {/* Change Password */}
      <Card className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <KeyRound className="w-5 h-5 text-afriperx-gold" />
            <div>
              <h2 className="font-syne text-lg font-bold text-gray-900">Change Password</h2>
              <p className="text-gray-500 text-sm">Update your login password securely with OTP verification</p>
            </div>
          </div>
          <Button
            onClick={() => {
              setShowPasswordDialog(true);
              setOtpRequested(false);
              setPasswordData({ current: '', new: '', confirm: '' });
              setPasswordOtp('');
              setPasswordOtpHint('');
            }}
            variant="outline"
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
            data-testid="change-password-btn"
          >
            <Lock className="w-4 h-4 mr-2" />
            Change Password
          </Button>
        </div>
      </Card>

      {/* OTP Verification Dialog */}
      <Dialog open={showOTPDialog} onOpenChange={setShowOTPDialog}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900 font-syne flex items-center gap-2">
              <Lock className="w-5 h-5 text-afriperx-gold" />
              Verify Your Identity
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-gray-600 text-sm">
              An OTP has been sent to your registered mobile number. Enter it below to confirm your profile changes.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-blue-800 text-sm font-medium">
                Changes being made:
              </p>
              <ul className="text-blue-700 text-xs mt-1 space-y-1">
                {Object.entries(pendingUpdates).map(([key, value]) => (
                  <li key={key}>
                    <span className="capitalize">{key.replace(/_/g, ' ')}</span>: {value}
                  </li>
                ))}
              </ul>
            </div>

            {otpHint && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-yellow-800 text-xs">
                  Demo OTP: <span className="font-bold">{otpHint}</span>
                </p>
              </div>
            )}

            <div>
              <Label className="text-gray-700">Enter OTP</Label>
              <Input
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="Enter 4-digit OTP"
                maxLength={4}
                className="mt-2 bg-white border-gray-300 text-gray-900 text-center text-2xl tracking-widest"
                data-testid="profile-otp-input"
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleVerifyOTP}
                disabled={submitting || otp.length < 4}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                data-testid="verify-otp-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                {submitting ? 'Verifying...' : 'Verify & Save'}
              </Button>
              <Button
                onClick={() => { setShowOTPDialog(false); setOtp(''); }}
                variant="outline"
                className="border-gray-300 text-gray-600"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={(open) => { if (!open) { setShowPasswordDialog(false); setOtpRequested(false); } }}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900 font-syne flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-afriperx-gold" />
              Change Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {!otpRequested ? (
              <>
                <div>
                  <Label className="text-gray-700">Current Password</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showCurrentPw ? 'text' : 'password'}
                      value={passwordData.current}
                      onChange={(e) => setPasswordData(p => ({ ...p, current: e.target.value }))}
                      placeholder="Enter current password"
                      className="bg-white border-gray-300 text-gray-900 pr-10"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPw(!showCurrentPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="text-gray-700">New Password</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showNewPw ? 'text' : 'password'}
                      value={passwordData.new}
                      onChange={(e) => setPasswordData(p => ({ ...p, new: e.target.value }))}
                      placeholder="Enter new password (min 4 characters)"
                      className="bg-white border-gray-300 text-gray-900 pr-10"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPw(!showNewPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="text-gray-700">Confirm New Password</Label>
                  <Input
                    type="password"
                    value={passwordData.confirm}
                    onChange={(e) => setPasswordData(p => ({ ...p, confirm: e.target.value }))}
                    placeholder="Confirm new password"
                    className="mt-1 bg-white border-gray-300 text-gray-900"
                    data-testid="confirm-password-input"
                  />
                  {passwordData.confirm && passwordData.new !== passwordData.confirm && (
                    <p className="text-red-500 text-xs mt-1">Passwords do not match</p>
                  )}
                </div>

                <Button
                  onClick={handleRequestPasswordOTP}
                  disabled={!passwordData.current || !passwordData.new || passwordData.new !== passwordData.confirm}
                  className="w-full bg-afriperx-green hover:bg-afriperx-green-dark text-white"
                  data-testid="request-password-otp-btn"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Verify with OTP
                </Button>
              </>
            ) : (
              <>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-green-800 text-sm">
                    An OTP has been sent to your registered mobile number. Enter it below to confirm the password change.
                  </p>
                </div>

                {passwordOtpHint && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-yellow-800 text-xs">
                      Demo OTP: <span className="font-bold">{passwordOtpHint}</span>
                    </p>
                  </div>
                )}

                <div>
                  <Label className="text-gray-700">Enter OTP</Label>
                  <Input
                    value={passwordOtp}
                    onChange={(e) => setPasswordOtp(e.target.value)}
                    placeholder="Enter 4-digit OTP"
                    maxLength={4}
                    className="mt-2 bg-white border-gray-300 text-gray-900 text-center text-2xl tracking-widest"
                    data-testid="password-otp-input"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleChangePassword}
                    disabled={submitting || passwordOtp.length < 4}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    data-testid="confirm-password-change-btn"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {submitting ? 'Changing...' : 'Change Password'}
                  </Button>
                  <Button
                    onClick={() => { setOtpRequested(false); setPasswordOtp(''); }}
                    variant="outline"
                    className="border-gray-300 text-gray-600"
                  >
                    Back
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
